if (!customElements.get('blur-banner')) {
  customElements.define('blur-banner',
    class extends HTMLElement {
      constructor() {
        super();
        this.hero = null;
        this.contentBottom = null;
        this.contentBottomContent = null;

        this.config = {
          windowHeight: window.innerHeight
        }

        this.rafId = null;
        this.isScrollHandlerScheduled = false;
      }

      connectedCallback() {
        this.hero = this.querySelector('.wt-blur-banner__hero');
        this.contentBottom = this.querySelector('.wt-blur-banner__content__bottom');
        this.contentBottomContent = this.contentBottom?.querySelector('.wt-blur-banner__content__bottom__content');
        this.contentTop = this.querySelector('.wt-blur-banner__content__top');
        if (!this.hero || !this.contentBottom || !this.contentBottomContent) {
          return;
        }

        // Optional scroll-triggered fade-in: reveal-at is how far up from
        // the BOTTOM of the screen the trigger line sits, as a % of
        // viewport height (e.g. 25 = a line 25% up from the bottom edge).
        // Absent = feature off (only the blur runs).
        this.revealAt = this.dataset.revealAt != null ? parseFloat(this.dataset.revealAt) / 100 : null;

        // How far (in viewport heights) you must have scrolled past the
        // top of this element before the background blur is even eligible
        // to turn on. Default 0 = no gate (old behavior). This element
        // itself is not sticky, so its own top tracks scroll 1:1 - unlike
        // the hero, which clamps at 0 once pinned and can't be used to
        // measure "how far past the top" you've scrolled.
        this.blurAt = this.dataset.blurAt != null ? parseFloat(this.dataset.blurAt) / 100 : 0;

        this.updateConfig();
        this.resizeHandler = () => this.updateConfig();
        this.scrollHandler = () => this.handleScroll();
        window.addEventListener('resize', this.resizeHandler);
        window.addEventListener('scroll', this.scrollHandler);
        // Set the correct initial state (in case the section loads already
        // scrolled into view) so nothing stays stuck invisible.
        this.toggleHeroFilter();
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.resizeHandler);
        window.removeEventListener('scroll', this.scrollHandler);
        if (this.rafId !== null) {
          cancelAnimationFrame(this.rafId);
          this.rafId = null;
        }
        this.isScrollHandlerScheduled = false;
      }

      handleScroll() {
        if (!this.isScrollHandlerScheduled) {
          this.isScrollHandlerScheduled = true;
          this.rafId = requestAnimationFrame(() => {
            this.toggleHeroFilter();
            this.isScrollHandlerScheduled = false;
            this.rafId = null;
          });
        }
      }

      updateConfig() {
        this.config = {
          windowHeight: window.innerHeight
        }
      }

      toggleHeroFilter() {
        if (!this.hero || !this.contentBottomContent) {
          return;
        }

        const contentBottomContentTop = this.contentBottomContent.getBoundingClientRect().top;

        // Gate blur eligibility on how far past this element's own top
        // you've scrolled (this element is normal-flow, not sticky, so
        // its top decreases 1:1 with scroll - a true "distance scrolled
        // past the top" measurement).
        const scrolledPastTop = Math.max(0, -this.getBoundingClientRect().top);
        const blurThreshold = this.config.windowHeight * this.blurAt;
        const blurEligible = scrolledPastTop >= blurThreshold;

        if (blurEligible && contentBottomContentTop < this.config.windowHeight) {
          this.hero.classList.add('wt-blur-banner__hero--filter-active');
        } else {
          this.hero.classList.remove('wt-blur-banner__hero--filter-active');
        }

        // Fade the bottom content in once its top scrolls up past the
        // trigger line (revealAt% up from the bottom of the screen);
        // fade back out scrolling up.
        if (this.revealAt !== null) {
          const triggerLine = this.config.windowHeight * (1 - this.revealAt);
          if (contentBottomContentTop < triggerLine) {
            this.contentBottom.classList.add('is-revealed');
          } else {
            this.contentBottom.classList.remove('is-revealed');
          }
        }

        // Fade the top content in as soon as this section starts entering
        // the viewport at all - it's the first thing seen, so it should
        // reveal early (not wait for a deep-scroll trigger like the bottom
        // content does). Same toggle (revealAt !== null) turns both on.
        if (this.revealAt !== null && this.contentTop) {
          const sectionEntering = this.getBoundingClientRect().top < this.config.windowHeight;
          this.contentTop.classList.toggle('is-revealed', sectionEntering);
        }
      }
    }
  );
}
